import { computed, inject, Injector, Type } from '@angular/core';
import {
  signalStore,
  withComputed,
  withFeature,
  withProps,
  withState,
} from '@ngrx/signals';

import {
  EXTERNAL_TODO_TASK_STATUSES,
  TaskInstance,
  TaskListSignalState,
} from '@data-access/tasks/types';
import { TasksService } from '@data-access/tasks/services';
import { DEFAULT_TASK_LIST_STATE } from '@data-access/tasks/constants';
import { withTaskEntities } from './entities';
import { DOCUMENT_TASKS } from '@data-access/tasks/constants/task.constants';
import { Dispatcher, Events } from '@ngrx/signals/events';
import { withTaskEffects } from '@data-access/tasks/store/signal-store/effects';
import {
  buildExternalRemainingQuestionToDoTasksMap,
  buildExternalRemainingSectionToDoTasksMap,
  filterOpenExternalToDoTasks,
  isExternalRemainingQuestionToDoTask,
  isExternalRemainingSectionToDoTask,
  isLegacyExternalRemainingQuestionToDoTask,
  isLegacyExternalRemainingSectionToDoTask,
  isOpenExternalToDo,
} from '../../utils/task-list.utils';
import {
  FEATURE_FLAGS_SIGNAL_STORE,
  FeatureFlag,
} from '@data-access/system-controls/feature-flags';
import { withDevtools } from '@angular-architects/ngrx-toolkit';

/*
 * @private
 * Tallies open customer to-dos (shared ERM-12595 predicate) per section id.
 **/
function _getOpenToDoTasksForSectionMap(
  tasks: TaskInstance[],
  sectionMap: Map<string, number>,
): Map<string, number> {
  return tasks.reduce((_map, task) => {
    const _sectionId = task.task_entity?.section_id;
    if (!_sectionId || !isOpenExternalToDo(task)) {
      return _map;
    }

    _map.set(_sectionId, (_map.get(_sectionId) ?? 0) + 1);
    return _map;
  }, sectionMap);
}

/*
 * @private
 * Flag-off tally: the legacy hub counts unresolved-thread tasks per section.
 **/
function _getUnresolvedCollabhubTasksForSectionMap(
  tasks: TaskInstance[],
  sectionMap: Map<string, number>,
): Map<string, number> {
  tasks
    .filter((task) => task.thread && !task.thread?.is_resolved)
    .filter((task) => task.status !== 'ready_for_review')
    .forEach((task) => {
      const sectionId = task.task_entity!.section_id!;
      sectionMap.set(sectionId, (sectionMap.get(sectionId) || 0) + 1);
    });
  return sectionMap;
}

export const TASK_LIST_SIGNAL_STORE = signalStore(
  withDevtools('taskList'), // Added for Redux DevTools integration
  withState(() => inject(DEFAULT_TASK_LIST_STATE)),
  withProps((_store, _taskListService = inject(TasksService)) => ({
    // ERM-12595: the redesign's status-based to-do semantics ride this flag
    _collabHubRedesign: inject(FEATURE_FLAGS_SIGNAL_STORE).$selectFeatureFlag(
      FeatureFlag.COLLAB_HUB_REDESIGN,
    ),
    _dispatcher: inject(Dispatcher),
    _events: inject(Events),
    _injector: inject(Injector),
    _taskListService,
  })),
  withFeature(({ _injector }) => withTaskEntities(_injector)),
  withFeature(({ _events, _taskListService, _dispatcher }) =>
    withTaskEffects(_events, _taskListService, _dispatcher),
  ),
  withComputed(
    ({
      _collabHubRedesign,
      _questionsEntities,
      _documentsEntities,
      _sectionsEntities,
      _taskEntities,
      isSMEQCLoading,
      tasksAlreadyLoaded,
      ...store
    }) => ({
      /** Computes any tasks with the type matching a known DOCUMENT_TASK type
       * @returns An array of document-related TaskInstance objects sorted by updated_dt descending
       * */
      $documentTasks: computed(() => {
        const taskEntities = _taskEntities();
        if (!taskEntities) return [];
        return taskEntities
          .filter((task) => DOCUMENT_TASKS.has(task.task_type))
          .sort(
            (a, b) =>
              new Date(b.updated_dt ?? 0).getTime() -
              new Date(a.updated_dt ?? 0).getTime(),
          );
      }),

      $error: computed(() => store.error()),

      $filtered: computed(() => store.filtered()),

      $isLoading: computed(() => store.loading()),

      /** Error from the main getTaskList request only. */
      $taskListError: computed(() => store.taskListError()),

      /** True while the main getTaskList request is in flight. */
      $isTaskListLoading: computed(() => store.taskListLoading()),

      $isSMEQCLoading: computed(() => isSMEQCLoading()),

      $openExternalToDoTasks: computed(() =>
        filterOpenExternalToDoTasks(store.remainingToDoTasks()),
      ),

      /** Every question/section/document to-do ever raised for the external
       * user on this submission, open or resolved — despite the "remaining"
       * name, `remainingToDoTasks()` holds the full non-cancelled fetch
       * (ERM-12647's `getRemainingToDoTaskCount` request has no status
       * filter narrower than non-cancelled), so this is a true total, not an
       * approximation from a thread- or entity-scoped list. Exists so
       * "resubmitted" can be told apart from "never had a to-do": both read
       * `$openExternalToDoCount() === 0`. */
      $totalExternalToDoCount: computed(() => {
        const remaining = store.remainingToDoTasks();
        return (
          (remaining?.question.length ?? 0) +
          (remaining?.section.length ?? 0) +
          (remaining?.document.length ?? 0)
        );
      }),

      /** Computes any tasks with the 'question' task entity type */
      $questionTasks: computed(() => {
        const _qTasks = _questionsEntities();
        if (!_qTasks) {
          return [];
        }
        return _qTasks;
      }),

      /** Computes any remaining 'document' SME QC tasks (internal, not completed) */
      $remainingDocumentSMEQCTasks: computed(() => {
        const remainingTasks = store.remainingSMEQCTasks();
        if (!remainingTasks) {
          return [];
        }
        return remainingTasks.document;
      }),

      /** Computes remaining document tasks (type 'document') as a Map */
      $remainingDocumentToDoTasks: computed(() => {
        const _remainingToDoTasks = store.remainingToDoTasks();
        const _documentTasks = _remainingToDoTasks?.document;
        if (!_documentTasks?.length) {
          return new Map<string, TaskInstance>();
        }
        return new Map<string, TaskInstance>(
          _documentTasks
            .filter(
              (task) =>
                task.thread &&
                !task.thread.is_resolved &&
                task.task_type === 'document',
            )
            .map((task) => [task.id, task]),
        );
      }),

      /** Computes remaining question tasks (type 'question') as a Map */
      $remainingQuestionToDoTasks: computed(() => {
        const _remainingToDoTasks = store.remainingToDoTasks();
        const _questionTasks = _remainingToDoTasks?.question;
        if (!_questionTasks?.length) {
          return new Map<string, TaskInstance>();
        }

        return new Map<string, TaskInstance>(
          _questionTasks
            .filter(
              (task) =>
                task.thread &&
                !task.thread.is_resolved &&
                EXTERNAL_TODO_TASK_STATUSES.includes(task.status) &&
                !!task.task_entity?.question_id,
            )
            .map((task) => [task.task_entity!.question_id!, task]),
        );
      }),

      /** Computes remaining external question tasks, including threadless conditional tasks */
      $remainingExternalQuestionToDoTasks: computed(() => {
        const _remainingToDoTasks = store.remainingToDoTasks();
        // Flag-off keeps the legacy thread-based membership (ERM-12595)
        return buildExternalRemainingQuestionToDoTasksMap(
          _remainingToDoTasks?.question ?? [],
          _collabHubRedesign()
            ? isExternalRemainingQuestionToDoTask
            : isLegacyExternalRemainingQuestionToDoTask,
        );
      }),

      /** Computes any remaining 'other' SME QCtasks (internal, not completed) */
      $remainingOtherSMEQCTasks: computed(() => {
        const remainingTasks = store.remainingSMEQCTasks();
        if (!remainingTasks) {
          return [];
        }
        return remainingTasks.other;
      }),

      /** Computes any remaining 'question' SME QC tasks (internal, not completed) */
      $remainingQuestionSMEQCTasks: computed(() => {
        const remainingTasks = store.remainingSMEQCTasks();
        if (!remainingTasks) {
          return [];
        }
        return remainingTasks.question;
      }),

      /** Computes remaining requested document tasks as a Map */
      $remainingRequestedDocumentToDoTasks: computed(() => {
        const _remainingToDoTasks = store.remainingToDoTasks();
        const _documentTasks = _remainingToDoTasks?.document;
        if (!_documentTasks?.length) {
          return new Map<string, TaskInstance>();
        }
        return new Map<string, TaskInstance>(
          _documentTasks
            .filter(
              (task) =>
                task.thread &&
                !task.thread.is_resolved &&
                task.task_type === 'document_type_requested',
            )
            .map((task) => [task.id, task]),
        );
      }),

      /** ERM-14106: Computes redesigned Collab Hub requested document to-dos from task status.
       * `incomplete` is deliberately excluded: only newly created and reopened
       * requested-document tasks are actionable in this view. */
      $remainingRequestedDocumentToDoTasksV2: computed(() => {
        const _remainingToDoTasks = store.remainingToDoTasks();
        const _documentTasks = _remainingToDoTasks?.document;
        if (!_documentTasks?.length) {
          return new Map<string, TaskInstance>();
        }
        return new Map<string, TaskInstance>(
          _documentTasks
            .filter(
              (task) =>
                task.task_type === 'document_type_requested' &&
                EXTERNAL_TODO_TASK_STATUSES.includes(task.status) &&
                task.status !== 'incomplete',
            )
            .map((task) => [task.id, task]),
        );
      }),

      /** Computes remaining section tasks (type 'section') as a Map */
      $remainingSectionToDoTasks: computed(() => {
        const _remainingToDoTasks = store.remainingToDoTasks();
        const _sectionTasks = _remainingToDoTasks?.section;
        if (!_sectionTasks?.length) {
          return new Map<string, TaskInstance>();
        }

        return new Map<string, TaskInstance>(
          _sectionTasks
            .filter(
              (task) =>
                task.thread &&
                !task.thread.is_resolved &&
                EXTERNAL_TODO_TASK_STATUSES.includes(task.status) &&
                !!task.task_entity?.section_id,
            )
            .map((task) => [task.task_entity!.section_id!, task]),
        );
      }),

      /** Computes remaining external section tasks, including threadless conditional tasks */
      $remainingExternalSectionToDoTasks: computed(() => {
        const _remainingToDoTasks = store.remainingToDoTasks();
        // Flag-off keeps the legacy thread-based membership (ERM-12595)
        return buildExternalRemainingSectionToDoTasksMap(
          _remainingToDoTasks?.section ?? [],
          _collabHubRedesign()
            ? isExternalRemainingSectionToDoTask
            : isLegacyExternalRemainingSectionToDoTask,
        );
      }),

      /** Computes any remaining SME QC tasks (internal, not completed) */
      $remainingSMEQCTasks: computed(() => {
        return store.remainingSMEQCTasks();
      }),

      /** Computes any remaining 'section' tasks (internal, not completed) */
      $remainingSectionSMEQCTasks: computed(() => {
        const remainingTasks = store.remainingSMEQCTasks();
        if (!remainingTasks) {
          return [];
        }
        return remainingTasks.section;
      }),

      /** Computes each section's open customer to-do count ('section' plus
       * 'question' tasks), on the shared ERM-12595 open-to-do predicate */
      $remainingSectionTaskTodoCount: computed(() => {
        const _remainingTodoTasks = store.remainingToDoTasks();
        if (!_remainingTodoTasks) {
          return new Map<string, number>([]);
        }
        // Flag-off keeps the legacy unresolved-thread tally (ERM-12595)
        const _tallySectionCounts = _collabHubRedesign()
          ? _getOpenToDoTasksForSectionMap
          : _getUnresolvedCollabhubTasksForSectionMap;
        const sectionTaskCountMap = new Map<string, number>();
        _tallySectionCounts(_remainingTodoTasks.section, sectionTaskCountMap);
        _tallySectionCounts(_remainingTodoTasks.question, sectionTaskCountMap);
        return sectionTaskCountMap;
      }),

      /** Computes any tasks that are marked as to-do (not completed, not internal) */
      $remainingToDoCollabHubTasks: computed(() => {
        return store.remainingToDoTasks();
      }),

      /** Computes any tasks with the 'section' task entity type */
      $sectionTasks: computed(() => {
        const _sectionTaskEntities = _sectionsEntities();
        if (!_sectionTaskEntities) {
          return [];
        }
        return _sectionTaskEntities;
      }),
      /** Computes all fetched tasks */
      $tasks: computed(() => {
        const taskEntities = _taskEntities();
        if (!taskEntities) {
          return [];
        }
        return taskEntities;
      }),
      /** Computes all tasks that are associated with a collabhub thread. Threads are ordered as unresolved first **/
      $threads: computed(() => {
        const taskEntities = _taskEntities();
        if (!taskEntities) {
          return [];
        }
        // Sort threads: unresolved first, then resolved
        return taskEntities
          .filter((task) => task.thread)
          .sort(
            (a, b) =>
              Number(a.thread?.is_resolved) - Number(b.thread?.is_resolved),
          );
      }),
      $tasksAlreadyLoaded: computed(() => tasksAlreadyLoaded()),
      $openExternalToDoCount: computed(() => {
        const openToDos = filterOpenExternalToDoTasks(
          store.remainingToDoTasks(),
        );
        return (
          openToDos.question.length +
          openToDos.section.length +
          openToDos.document.length
        );
      }),

      $remainingToDoDetailTaskCount: computed(() => {
        const openToDos = filterOpenExternalToDoTasks(
          store.remainingToDoTasks(),
        );
        return openToDos.question.length + openToDos.section.length;
      }),
      $remainingToDoDocumentTaskCount: computed(() => {
        const openToDos = filterOpenExternalToDoTasks(
          store.remainingToDoTasks(),
        );
        return openToDos.document.length;
      }),
    }),
  ),
) satisfies Type<TaskListSignalState>;
